
import { SPACE } from '../../../constants'
import { WEB_MESSAGE_RESPONSE_TYPE } from '../constants'
import { PostMessageResponse, SessionState } from '../constants/types'
import { Session } from '../session'

const SECOND = 1000

type TimeOut = ReturnType<typeof setTimeout>

export default class HiddenIframe {
  el: HTMLIFrameElement
  timer: TimeOut | null
  id: string

  constructor() {
    this.id = 'nn-iframe-window'
    this.el = document.createElement('iframe')
    this.el.width = '0'
    this.el.height = '0'
    this.el.id = this.id
    this.el.style.position = 'fixed'
    this.el.style.visibility = 'hidden'
    this.timer = null
  }

  public open(url = SPACE, id: string, timeout = SECOND * 2): Promise<SessionState> {
    return new Promise((resolve, reject) => {
      const timeOutFn = () => {
        this.timer = setTimeout(reject, timeout)
      }

      const handler = (event: MessageEvent<PostMessageResponse>) => {
        const { data, origin } = event

        if (data && data.type === WEB_MESSAGE_RESPONSE_TYPE && data.response.state === id) {
          clearTimeout(this.timer as TimeOut)
          window.removeEventListener('message', handler)

          resolve({ ...data.response, origin })
        }
      }

      window.addEventListener('message', handler, false)

      this.el.onerror = reject
      this.el.onload = () => {
        timeOutFn()
      }

      this.el.src = url

      if (document.body) {
        const oldIframe = document.getElementById(this.id)
        oldIframe && document.body.removeChild(oldIframe)
        document.body.appendChild(this.el)
      }
    })
  }

  public close(): void {
    if (document.body && document.getElementById(this.id)) {
      document.body.removeChild(this.el)
    }
  }
}

export class CheckSession extends Session {
  constructor(timeout: number) {
    super('', '', '', timeout)
  }

  use(clientId: string, sessionState: string, iframeUrl: string, origin: string) {
    this.client = clientId
    this.session = sessionState
    this.url = iframeUrl
    this.origin = origin

    return this
  }
}

export function isIframe() {
  try {
    // Check if the window object exists and has a different reference than window.top
    if (window && window.self !== window.top) {
      return true
    }

    // Check if the window.frameElement exists
    if (window.frameElement !== null) {
      return true
    }

    // Check if the document's defaultView is not the same as window
    if (document.defaultView && document.defaultView !== window) {
      return true
    }

    // Check if the parent window is not null and is different from the current window
    if (window.parent && window.parent !== window) {
      return true
    }

    // If none of the above conditions are met, assume it's not in an iframe
    return false
  } catch (error) {
    // This catch block is needed to handle cross-origin security restrictions.
    return true // If an error occurs, it's likely due to being in an iframe.
  }
}
